// Route 2 — How We Help: plain-English situation pages.
// Short pages that reassure, then point visitors to the detailed offence pages.

export const SITUATIONS = [
  {
    slug: "ive-been-arrested",
    title: "I've been arrested",
    icon: "Siren",
    urgent: true,
    lead: "Take a breath. Being arrested does not mean you will be charged — and what happens in the next few hours matters more than anything that has happened so far.",
    body: "You have the right to free, independent legal advice at the police station, whatever your means. You do not have to answer police questions until your solicitor is with you — and you shouldn't. We attend police stations across London at any hour.",
    steps: [
      { title: "Say nothing yet", text: "Politely decline to answer questions until your solicitor arrives. Anything you say can shape the whole case." },
      { title: "Ask for Hallinans", text: "Tell the custody officer you want Hallinans to represent you, or have a family member call our 24 hour line: 07831 406 242." },
      { title: "We take it from here", text: "We will attend or arrange representation immediately, advise you before and during interview, and protect your position from the first minute." },
    ],
    related: [
      { label: "General Crime", href: "/services/general-crime" },
      { label: "Assault & Violent Offences", href: "/services/general-crime/assault-violent-offences" },
      { label: "Drug Offences", href: "/services/general-crime/drug-offences" },
    ],
  },
  {
    slug: "voluntary-interview",
    title: "I've been asked to a voluntary interview",
    icon: "MessageCircleQuestion",
    urgent: false,
    lead: "A 'voluntary' interview sounds informal. It isn't. It is an interview under caution — everything you say can be used in evidence, exactly as if you had been arrested.",
    body: "Police increasingly use voluntary interviews instead of arrest. Attending without a solicitor is a serious mistake: you may talk your way into a charge that careful preparation would have avoided. Legal advice at the police station is free for everyone, regardless of income.",
    steps: [
      { title: "Don't attend alone", text: "Never attend a voluntary interview without legal representation — it is free and it is your right." },
      { title: "Call us first", text: "Contact us before confirming any date. We will liaise with the police, obtain pre-interview disclosure and prepare you properly." },
      { title: "We advise in the room", text: "We sit beside you throughout, advise on every question, and intervene where the questioning is unfair." },
    ],
    related: [
      { label: "Serious Fraud & Financial Crime", href: "/services/serious-specialist-crime/serious-fraud" },
      { label: "Sexual Offences", href: "/services/general-crime/sexual-offences" },
      { label: "General Crime", href: "/services/general-crime" },
    ],
  },
  {
    slug: "under-investigation",
    title: "I'm under investigation",
    icon: "SearchCheck",
    urgent: false,
    lead: "Whether you are on police bail or released under investigation, the waiting is hard — and investigations can run for months or even years. You do not have to face that period passively.",
    body: "Early, proactive defence work during the investigation stage can shape whether you are ever charged at all. We engage with investigators, make pre-charge representations, chase updates so you are never left in the dark, and prepare your case so that you are ready for whatever comes.",
    steps: [
      { title: "Get advice now, not at charge", text: "The investigation stage is where cases are won quietly. Pre-charge engagement can result in no further action being taken." },
      { title: "Understand your bail position", text: "Police bail has time limits and conditions that can be challenged; release under investigation has neither — we explain exactly where you stand." },
      { title: "Stay informed", text: "We maintain contact with the investigation team and keep you updated on every development." },
    ],
    related: [
      { label: "Serious Fraud & Financial Crime", href: "/services/serious-specialist-crime/serious-fraud" },
      { label: "Proceeds of Crime & Restraint", href: "/services/serious-specialist-crime/proceeds-of-crime" },
      { label: "Regulatory & Investigations", href: "/services/regulatory-investigations" },
    ],
  },
  {
    slug: "ive-been-charged",
    title: "I've been charged",
    icon: "FileText",
    urgent: false,
    lead: "Being charged is frightening — but it is the start of a process, not the end of one. Every case starts in the Magistrates' Court, and the decisions you make now set the course of everything that follows.",
    body: "Your first hearing will usually take place quickly. Before it, we will advise you on the strength of the evidence, your plea, where your case will be heard, funding (private or legal aid), and bail. From minor matters to murder, we conduct criminal litigation from the Magistrates' Court through to the Supreme Court.",
    steps: [
      { title: "Send us the paperwork", text: "Your charge sheet and postal requisition tell us the charges and your hearing date — we take it from there." },
      { title: "Resolve funding early", text: "We act for both privately and publicly funded clients and hold our own Legal Aid Agency contract. See Fees & Funding." },
      { title: "Prepare for the first hearing", text: "We advise on plea and venue before you step into court, so nothing is decided unprepared." },
    ],
    related: [
      { label: "Our Services", href: "/services" },
      { label: "Fees & Funding", href: "/fees-funding" },
      { label: "What is a PTPH? (guide)", href: "/resources/procedure/ptph-and-the-crown-court" },
    ],
  },
  {
    slug: "court-date",
    title: "I have a court date",
    icon: "CalendarClock",
    urgent: false,
    lead: "A court date on the calendar can feel overwhelming. Knowing exactly what will happen in the room — and being properly prepared for it — changes everything.",
    body: "Whether it is a first appearance at the Magistrates' Court, a Plea and Trial Preparation Hearing (PTPH) in the Crown Court, or trial itself, we prepare you thoroughly: what the hearing is for, what you will be asked, who will be there, and what happens next. Read our plain-English guide to Crown Court procedure in Legal Resources.",
    steps: [
      { title: "Contact us before the hearing", text: "Even if the date is close, call us. Late instruction is better than no representation." },
      { title: "Know the listing", text: "We confirm the time and courtroom the working day before, and tell you exactly when and where to attend." },
      { title: "Walk in prepared", text: "We meet you at court, conference with you beforehand, and represent you throughout the hearing." },
    ],
    related: [
      { label: "What is a PTPH? (guide)", href: "/resources/procedure/ptph-and-the-crown-court" },
      { label: "General Crime", href: "/services/general-crime" },
      { label: "Our Services", href: "/services" },
    ],
  },
  {
    slug: "my-child-is-in-trouble",
    title: "My child is in trouble with the police",
    icon: "Users",
    urgent: true,
    lead: "Nothing is more frightening than your child being arrested or investigated. The youth justice system is different from the adult system — and handled properly, one mistake does not have to define a young person's future.",
    body: "Children have additional protections at the police station, including the right to an appropriate adult, and the Youth Court works under its own procedures and sentencing framework. We represent under-18s with sensitivity and specialist care, always with their welfare and future at the centre.",
    steps: [
      { title: "Insist on legal advice", text: "It is free at the police station and your child should never be interviewed without it. Call our 24 hour line." },
      { title: "Be the appropriate adult — or we'll arrange one", text: "A parent or guardian can usually act as appropriate adult; we will guide you on the role." },
      { title: "Think long-term", text: "We pursue outcomes that protect your child's record, education and future — including out-of-court disposals where possible." },
    ],
    related: [
      { label: "Youth Justice", href: "/services/youth-justice" },
      { label: "I've been arrested", href: "/how-we-help/ive-been-arrested" },
    ],
  },
  {
    slug: "i-want-to-appeal",
    title: "I want to appeal",
    icon: "RotateCcw",
    urgent: false,
    lead: "A conviction or sentence is not necessarily the end of the road — but the clock is already running. Appeal time limits are short and strict.",
    body: "Notice of appeal from the Crown Court must generally be lodged within 28 days; from the Magistrates' Court, within 15 business days. We advise on the merits quickly and honestly — including second opinions where another firm acted at trial — and we have taken cases to the Court of Appeal and the Supreme Court.",
    steps: [
      { title: "Act immediately", text: "Contact us as soon as possible after conviction or sentence — out-of-time applications need compelling reasons." },
      { title: "Get an honest merits assessment", text: "We will tell you plainly whether there are arguable grounds, and what the realistic outcomes are." },
      { title: "Consider every route", text: "Appeal, judicial review or the Criminal Cases Review Commission — we identify the right route for your case." },
    ],
    related: [
      { label: "Appeals", href: "/services/appeals" },
      { label: "The UK Supreme Court (guide)", href: "/resources" },
    ],
  },
  {
    slug: "urgent-help",
    title: "I need help urgently",
    icon: "PhoneCall",
    urgent: true,
    lead: "If you or someone you care about needs a criminal defence solicitor right now, call our emergency line. It is answered 24 hours a day, every day.",
    body: "Whether it is an arrest in the middle of the night, a dawn raid, an urgent court production or a search warrant being executed — speak to us before you speak to anyone else. We will act immediately.",
    steps: [
      { title: "Call 07831 406 242", text: "Our 24 hour emergency line connects you directly to the team — day or night." },
      { title: "Say nothing until we arrive", text: "You have the right to remain silent and the right to free legal advice. Use both." },
      { title: "We take it from here", text: "We attend police stations across London and act immediately to protect your position." },
    ],
    related: [
      { label: "I've been arrested", href: "/how-we-help/ive-been-arrested" },
      { label: "Contact us", href: "/contact" },
    ],
  },
];

export const getSituation = (slug) => SITUATIONS.find((s) => s.slug === slug);
